import {
  Box,
  CircularLoader,
  DropDown,
  DropDownItem,
  IconTextLink,
  PrimaryButton,
  Typography,
} from "@husqvarna/ui-core";
import ProjectEditDialog from "../../../../project/components/ProjectEditDialog";
import { useIntl } from "react-intl";
import { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { ReactComponent as TalkBubble } from "../../../icons/talkBubble.svg";
import Project from "../../../../project/models/Project";
import { clearTrackingData } from "../../../utils/tracking";

type RightNavigationProps = {
  selectedProject: Project | undefined;
  isExporting: boolean;
  onExport: () => void;
  onExportInstallationJson: () => void;
  onOpenAbout: () => void;
};

const RightNavigation = ({
  selectedProject,
  isExporting,
  onExport,
  onExportInstallationJson,
  onOpenAbout,
}: RightNavigationProps) => {
  const intl = useIntl();
  const [editProjectOpen, setEditProjectOpen] = useState(false);
  const saved = useSelector((state: RootState) => state.project.saved);
  const isSaving = useSelector((state: RootState) => state.project.isSaving);

  const handleLogout = () => {
    clearTrackingData();
    window.location.assign("/api/authentication/logout");
  };

  const handleDialogClose = () => {
    if (selectedProject) {
      setEditProjectOpen(false);
    }
  };
  const feedbackLink = () => (
    <a href={intl.formatMessage({
            id: "header.feedbackLink",
            defaultMessage: "https://hsq-prod.husqvarnagroup.com/TurfCarePlannerFeedbackInputForm/",
          })} target="_blank" rel="noreferrer"
          className="tcsp-header__feedback-link">
          <span aria-hidden="true">
            <TalkBubble width={16} height={16} />
            </span>
            <span>{intl.formatMessage({
            id: "header.feedbackLinkText",
            defaultMessage: "Feedback",
          })}</span>
        </a>
  );

  return (
    <Box
      className="tcsp-header__right"
      container
      alignItems="center"
      mx={6}
    >
      {selectedProject && (
        <>
          {isSaving && (
            <Box flexDirection="row"gap={2} alignItems="center" mr={4}>
              <CircularLoader size={4} strokeWidth={3} />
              <Typography variant="Body2" color="Grey500" text={intl.formatMessage({id: "header.saving", defaultMessage: "Saving"})} />
            </Box>
          )}

          {saved && !isSaving && (
            <Box flexDirection="row"gap={2} alignItems="center" mr={4}>
              <Typography variant="Body2" text={intl.formatMessage({id: "header.changesSaved", defaultMessage: "Saved"})} color="Grey500" />
            </Box>
          )}
          <Box
            className="tcsp-header__divider"
            backgroundColor="Grey200"
            ml={1}
            mr={4}
          />
          <IconTextLink
            className="tcsp-header__edit-project"
            iconId="pen"
            variant="Body2"
            onClick={() => setEditProjectOpen(true)}
            text={selectedProject.name}
          />
          <ProjectEditDialog
            project={selectedProject}
            isOpen={editProjectOpen}
            onClosing={() => handleDialogClose()}
          />
          {feedbackLink()}
          <Box
            flexDirection="row"
            gap={2}
            alignItems="center"
            ml={3}
          >
            {isExporting && (
              <PrimaryButton loading={true} className="tcsp-header__export-button-loading" />
            )}
            {!isExporting && (
              <div
                className="tcsp-header__export-dropdown"
                style={{
                  pointerEvents: isExporting ? "none" : undefined,
                  opacity: isExporting ? 0.65 : 1,
                }}
              >
                <DropDown
                  closeOnSelect
                  variant="Body2"
                  alignDrop="right"
                  className="export-button primary"
                  iconId="angle-down"
                  text={intl.formatMessage({
                    id: "header.exporProject",
                    defaultMessage: "Export",
                  })}
                >
                  <DropDownItem
                    text={intl.formatMessage({
                      id: "general.exportPowerpoint",
                      defaultMessage: "Sales presentation (.pptx)",
                    })}
                    onSelected={() => onExport()}
                  />
                  <DropDownItem
                    text={intl.formatMessage({
                      id: "general.exportJson",
                      defaultMessage: "For installation (.json)",
                    })}
                    onSelected={() => onExportInstallationJson()}
                  />
                </DropDown>
              </div>
            )}
          </Box>
        </>
      )}
      {!selectedProject && (
        <>
          {feedbackLink()}
          <IconTextLink
            iconId="question"
            className="tcsp-header__link"
            onClick={() => onOpenAbout.call(this)}
            text={intl.formatMessage({
              id: "header.aboutLinkText",
              defaultMessage: "About",
            })}
            variant="Body2"
          />
          
          <IconTextLink
            className="tcsp-header__link tcsp-header__signout-link"
            iconId="download"
            href="/api/authentication/logout"
            onClick={() => handleLogout()}
            text={intl.formatMessage({
              id: "header.signOutLinkText",
              defaultMessage: "Sign out",
            })}
            variant="Body2"
          />
        </>
      )}
    </Box>
  );
};

export default RightNavigation;
